import { useQuery } from "@tanstack/react-query";
import { api, money } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import { CurrencyDollar, TrendUp, ArrowUp, ArrowDown } from "@phosphor-icons/react";

const AXIS = { fontSize: 11, fill: "#94a3b8" };

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <Card className="p-5 bg-card border-border">
      <div className="flex items-center justify-between mb-2">
        <span className="overline text-muted-foreground">{label}</span>
        <div className={`h-8 w-8 rounded-md flex items-center justify-center ${accent}`}><Icon className="h-4 w-4" weight="bold" /></div>
      </div>
      <p className="text-2xl font-black">{value}</p>
    </Card>
  );
}

export default function AdminRevenue() {
  const { data } = useQuery({
    queryKey: ["admin-revenue"],
    queryFn: async () => (await api.get("/admin/revenue")).data,
  });
  if (!data) return <div className="text-muted-foreground animate-pulse">Loading…</div>;
  const cur = "USD";
  const hasRevenue = data.monthly_trend.some((m) => m.revenue > 0);

  return (
    <div data-testid="admin-revenue-page">
      <p className="text-xs text-muted-foreground mb-4">{data.note}</p>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <Stat icon={CurrencyDollar} label="MRR" value={money(data.mrr, cur)} accent="bg-emerald-500/20 text-emerald-400" />
        <Stat icon={TrendUp} label="ARR" value={money(data.arr, cur)} accent="bg-primary/20 text-primary" />
        <Stat icon={ArrowUp} label="New This Month" value={data.new_subscriptions_this_month} accent="bg-blue-500/20 text-blue-400" />
        <Stat icon={ArrowDown} label="Churned This Month" value={data.churned_this_month} accent="bg-destructive/20 text-destructive" />
      </div>
      <Card className="p-5 bg-card border-border" data-testid="revenue-chart">
        <h3 className="text-base font-bold mb-4">Monthly Revenue — Last 12 Months</h3>
        {hasRevenue ? (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.monthly_trend} margin={{ top: 5, right: 8, left: -12, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
                <XAxis dataKey="month" tick={AXIS} axisLine={false} tickLine={false} />
                <YAxis tick={AXIS} axisLine={false} tickLine={false} width={44} />
                <Tooltip cursor={{ fill: "#1e293b55" }} contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="revenue" fill="#34d399" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="h-40 flex items-center justify-center text-muted-foreground" data-testid="revenue-placeholder">
            Data available after Prompt 8
          </div>
        )}
      </Card>
    </div>
  );
}
